// Per-service copy for the engage-an-expert flow. Kept as data so the chooser stays a
// single renderer instead of branching markup three ways.

import { ENGAGEMENT_MIN_HOURS, SERVICE_COSTS, formatHourPrice, formatUSD } from '@/utils/expertHours';

export type EngageServiceKey = 'development' | 'migration' | 'production';

export interface ServiceCopy {
    heading: string;
    subheading: string;
    contextTitle: string;
    contextBody: string;
    scope?: { included: string[]; excluded: string[] };
    steps: string[];
    /** Server category key; migration and production have no key of their own. */
    category: string;
    /** Prefix that keeps the real intent readable for whoever picks up the request. */
    descriptionPrefix?: string;
}

export const SERVICE_COPY: Record<EngageServiceKey, ServiceCopy> = {
    development: {
        heading: 'Development support',
        subheading: 'An architect works inside this project alongside you.',
        contextTitle: 'You only pay for the time actually worked',
        contextBody: `An engagement needs ${ENGAGEMENT_MIN_HOURS} hours available to start, which is the least an architect can do anything meaningful in. Only logged time leaves your balance, so if the job comes in under, the rest stays yours for other projects.`,
        scope: {
            included: [
                'Work on a branch inside this project',
                'Fixing what is broken and explaining why',
                'A written handover in plain language',
                'Scoping conversations, never charged',
            ],
            excluded: [
                'Merging anything without your approval',
                'Work outside this brief, we come back and ask',
                'Ongoing monitoring once you are live',
                'Design or copywriting',
            ],
        },
        steps: [
            'An architect is assigned within one business day and confirms the plan in your project chat before touching anything.',
            'They work on a branch. Your app keeps running and you watch the changes land.',
            'You review and merge. Nothing ships without you pressing the button.',
            'If the job needs more than your balance holds, they stop and ask before going further.',
        ],
        category: 'other',
    },

    migration: {
        heading: 'Move your project onto Joylo',
        subheading: 'One fixed price, whatever it takes.',
        contextTitle: 'One price, agreed before we start',
        contextBody: `${formatUSD(SERVICE_COSTS.migration.displayUSD)} covers the migration however long it takes, and commits ${SERVICE_COSTS.migration.hours} architect hours to it. No hourly billing and no follow-up invoice because your project turned out more complicated than it looked. Your existing app stays live throughout.`,
        scope: {
            included: [
                'Code, database and assets moved onto Joylo',
                'Environment variables, auth and integrations rewired and tested',
                'Deployed and confirmed working, not just uploaded',
                'A walkthrough call so you know your way around',
            ],
            excluded: [
                'Rewriting features while we port them',
                'New functionality, that is development support',
                'Ongoing monitoring once you are live',
            ],
        },
        steps: [
            'An architect is assigned within one business day and picks up from whatever Import could not resolve.',
            'Migration runs in the background. Your existing app stays live the whole time.',
            'You get a walkthrough call once it is running on Joylo.',
            'You approve the result before we switch anything over.',
        ],
        category: 'integration',
        descriptionPrefix: 'Migration support',
    },

    production: {
        heading: 'Keep it running after launch',
        subheading: 'Monitoring and incident response for your live app.',
        contextTitle: 'Cover is bought a month at a time',
        contextBody: 'We watch your published app and step in when something breaks. Each month of cover commits architect hours up front, and unused hours stay in your balance. If code a Joylo architect wrote breaks, that is warranty and it is free either way.',
        scope: {
            included: [
                'Uptime, database health and error alerts',
                'Getting it back up, up to 4 hours per incident',
                'Every incident itemised in your dashboard',
            ],
            excluded: [
                'Improvements dressed up as repairs, we agree those first',
                'New features or redesigns',
                'Automatic renewal, you choose to extend each month',
            ],
        },
        steps: [
            'We connect monitoring to your live app. Takes about an hour and needs nothing from you.',
            'You get a baseline report showing what we found and anything worth fixing now.',
            'Alerts come to us first, not you. Most things are resolved before you notice.',
            'At the end of the month you decide whether to extend.',
        ],
        category: 'other',
        descriptionPrefix: 'Production support',
    },
};

export interface ProductionTier {
    key: 'production_essential' | 'production_advanced';
    name: string;
    items: { text: string; dim?: boolean }[];
}

export const PRODUCTION_TIERS: ProductionTier[] = [
    {
        key: 'production_essential',
        name: 'Essential',
        items: [
            { text: 'Uptime checked every minute' },
            { text: 'Database health and storage alerts' },
            { text: 'Error and crash alerts' },
            { text: 'Response within 4 business hours' },
        ],
    },
    {
        key: 'production_advanced',
        name: 'Advanced',
        items: [
            { text: 'Everything in Essential', dim: true },
            { text: 'Security monitoring and patching' },
            { text: 'Backups taken and restore tested' },
            { text: 'Performance tuning as traffic grows' },
            { text: 'Response within 2 business hours' },
        ],
    },
];

export const CO_BUILD_UPSELL = {
    title: 'Need this much every month?',
    body: `Co-Build 40 includes 40 architect hours a month, Advanced production support, a named architect who already knows your codebase, and a four hour response time. Past 80 hours it works out cheaper per hour than buying blocks at ${formatHourPrice(1)}/hr.`,
};
